import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { SEO } from '../components/SEO';
import { KeyRound, BookOpen, ExternalLink, AlertCircle, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../lib/apiBase';
const STORAGE_KEY = 'akademia_access_code';
const SESSION_KEY = 'akademia_session_v1';

// Stale-while-revalidate: ha van helyileg eltarolt teljes session
// (kod + kurzus-nev + 18 modul), azt azonnal megmutatjuk a felhasznalonak,
// es a hatterben silent modban ujraellenorizzuk a kodot a backenden.
// Igy network/szerver hiba eseten sem dobjuk vissza a felhasznalot.
const loadCachedSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.code === 'string' &&
      typeof parsed.courseName === 'string' &&
      Array.isArray(parsed.modules) &&
      parsed.modules.length > 0
    ) {
      return parsed;
    }
  } catch (e) {
    // corrupt JSON or storage unavailable — ignore, kezdjuk tisztan
  }
  return null;
};

const clearStoredSession = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_KEY);
  } catch (e) {
    // storage unavailable, ignore
  }
};

export const MaterialsPage = () => {
  // Mount-kor azonnal beolvassuk a cached session-t es atadjuk a state-nek,
  // igy a felhasznalo a 18 modult MAR az elso render-ben latja, meg mielott
  // a backend valaszolna.
  const cached = loadCachedSession();
  const [code, setCode] = useState(cached?.code || '');
  const [loading, setLoading] = useState(false);
  const [courseName, setCourseName] = useState(cached?.courseName || '');
  const [modules, setModules] = useState(cached?.modules || null);

  const validateCode = useCallback(async (codeToValidate, silent = false) => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/access/validate`, {
        code: codeToValidate,
      });
      const courseNameFromApi = res.data.course_name;
      const modulesFromApi = res.data.modules;

      setCourseName(courseNameFromApi);
      setModules(modulesFromApi);

      try {
        localStorage.setItem(STORAGE_KEY, codeToValidate);
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            code: codeToValidate,
            courseName: courseNameFromApi,
            modules: modulesFromApi,
          })
        );
      } catch (e) {
        // storage unavailable (incognito quota, stb.) — ignore
      }

      if (!silent) {
        toast.success('Hozzáférés engedélyezve');
      }
    } catch (error) {
      const status = error.response?.status;
      const isInvalidCode = status === 404 || status === 403;

      if (!silent) {
        const msg = error.response?.data?.detail || 'Hiba történt, próbáld újra';
        toast.error(msg);
      }

      // CSAK akkor toroljuk a mentett kodot/sessiont es dobjuk vissza a
      // felhasznalot, ha a backend explicit azt mondta hogy ervenytelen/lejart
      // a kod. Network / szerver hiba / timeout eseten hagyjuk bekenn a
      // stale state-et — a kovetkezo betoltesnel ujra megprobaljuk.
      if (isInvalidCode) {
        clearStoredSession();
        setModules(null);
        setCourseName('');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const savedCode = localStorage.getItem(STORAGE_KEY);
    if (savedCode) {
      setCode(savedCode);
      // Silent re-validate: ha a session cache friss es az ervenyes,
      // a felhasznalo eszre sem veszi. Ha invalid, dobjuk vissza.
      validateCode(savedCode, true);
    }
  }, [validateCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error('Add meg a kódot');
      return;
    }
    validateCode(trimmed);
  };

  const handleLogout = () => {
    clearStoredSession();
    setModules(null);
    setCourseName('');
    setCode('');
  };

  return (
    <main className="min-h-screen pt-32 pb-20" data-testid="materials-page">
      <SEO
        title="Tananyagok"
        description="Oktatási anyagok az Akadémia képzéseinek résztvevői számára."
        path="/tananyagok"
        noindex
      />

      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <p className="text-brand-light-green font-mono text-sm tracking-widest uppercase mb-4">
            Oktatási anyagok
          </p>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-4">
            Tananyagok
          </h1>
          <p className="text-white/60 text-lg">
            {modules
              ? `${courseName} — válassz modult`
              : 'Add meg a képzésen kapott hozzáférési kódot.'}
          </p>
        </motion.div>

        {!modules && (
          <div className="max-w-md mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <form onSubmit={handleSubmit} className="glass-card p-6 md:p-8" data-testid="code-form">
                <div className="flex items-center gap-3 mb-6">
                  <KeyRound size={24} className="text-brand-light-green" />
                  <h2 className="font-heading text-xl text-white font-semibold">
                    Hozzáférési kód
                  </h2>
                </div>

                <div className="space-y-5">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Add meg a kódot"
                      required
                      autoFocus
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      className="input-underline tracking-widest text-lg font-mono"
                      data-testid="code-input"
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck="false"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full disabled:opacity-50"
                    data-testid="code-submit"
                  >
                    {loading ? 'Ellenőrzés...' : 'Hozzáférés'}
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-white/50 text-sm flex items-start gap-2">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-white/30" />
                    <span>
                      A kódot a képzésen kapod meg. Ha még nem regisztráltál képzésre,{' '}
                      <a
                        href="/jelentkezes"
                        className="text-brand-light-green hover:underline"
                      >
                        jelentkezz itt
                      </a>
                      .
                    </span>
                  </p>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {modules && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <div className="glass-card p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-light-green/20 rounded-full flex items-center justify-center">
                  <BookOpen size={20} className="text-brand-light-green" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{courseName}</p>
                  <p className="text-white/50 text-xs">{modules.length} modul</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
                data-testid="materials-logout"
              >
                <LogOut size={16} />
                Kilépés
              </button>
            </div>

            <div className="glass-card p-6 md:p-8">
              <p className="text-white/70 mb-6">
                A modul címére kattintva új lapon megnyílik a Google Drive mappa, ahol megtalálod
                az oktatási videókat és segédanyagokat.
              </p>

              <ol className="space-y-3" data-testid="materials-list">
                {modules.map((m) => (
                  <li key={m.order}>
                    <a
                      href={m.drive_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-brand-light-green/30 transition-all group"
                      data-testid={`module-link-${m.order}`}
                    >
                      <span className="font-mono text-brand-light-green text-sm w-8 flex-shrink-0">
                        {String(m.order).padStart(2, '0')}.
                      </span>
                      <span className="text-white flex-1 group-hover:text-brand-light-green transition-colors">
                        {m.title}
                      </span>
                      <ExternalLink
                        size={16}
                        className="text-white/30 group-hover:text-brand-light-green flex-shrink-0 transition-colors"
                      />
                    </a>
                  </li>
                ))}
              </ol>
            </div>

            <div className="glass-card p-6 mt-6">
              <div className="flex items-start gap-3">
                <AlertCircle size={18} className="text-white/40 flex-shrink-0 mt-0.5" />
                <p className="text-white/40 text-sm">
                  Az oktatási anyagok kizárólag a képzés résztvevői számára elérhetők. Az anyagok
                  másolása, terjesztése vagy harmadik félnek történő átadása tilos.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default MaterialsPage;
